import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
  effect,
  computed,
  ElementRef,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductsService } from '../../../services/products.service';
import { PaymentsService } from '../../../services/payment.service';
import { SalesService, ReceiptFormat } from '../../../services/sales.service';
import { Product, ProductSaleItem, Client } from '../../../shared/interfaces';
import { ToastService } from '../../../shared/services';
import { ClientLookup } from '../../../shared/components/client-lookup/client-lookup';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

@Component({
  selector: 'app-product-sales-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ClientLookup],
  templateUrl: './product-sales-modal.html',
  styleUrl: './product-sales-modal.css',
})
export class ProductSalesModal implements OnInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Input() clientId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saleCompleted = new EventEmitter<void>();

  private productsService = inject(ProductsService);
  private paymentsService = inject(PaymentsService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);
  private salesService = inject(SalesService);

  // Generic client (system-wide, created via migration)
  private static readonly GENERIC_CLIENT_ID = '10000000-0000-0000-0000-000000000001';
  private static readonly GENERIC_CLIENT: Client = {
    userId: ProductSalesModal.GENERIC_CLIENT_ID,
    firstName: 'Cliente',
    lastName: 'Genérico',
    email: '',
    phone: '',
    isActive: true,
  };

  // View state
  modalState = signal<'sale' | 'success'>('sale');
  /** On phones only one pane shows at a time: the catalog or the ticket */
  mobilePane = signal<'catalog' | 'ticket'>('catalog');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  useGenericClient = signal(true);

  // Data
  products = signal<Product[]>([]);
  selectedItems = signal<ProductSaleItem[]>([]);
  selectedClient = signal<Client | null>(ProductSalesModal.GENERIC_CLIENT);

  // Loading states
  isLoading = signal(false);
  isSearching = signal(false);
  isSubmitting = signal(false);
  isDownloadingReceipt = signal(false);

  // Sale result
  lastPaymentId = signal<string | null>(null);
  lastSaleTotal = signal(0);
  lastPaidAmount = signal(0);

  /** Products added to the sale, kept so names and stock survive a new search */
  private cartProducts = new Map<string, Product>();

  // Forms
  searchForm = this.fb.group({
    searchTerm: [''],
  });

  paymentForm = this.fb.group({
    paidAmount: [0, [Validators.required, Validators.min(0)]],
    paymentMethod: ['cash', Validators.required],
    notes: [''],
  });

  // Payment method options
  paymentMethods = [
    { value: 'cash', label: 'Efectivo', icon: '💵' },
    { value: 'card', label: 'Tarjeta', icon: '💳' },
    { value: 'transfer', label: 'Transferencia', icon: '🏦' },
    { value: 'credit', label: 'Saldo a favor', icon: '👛' },
    { value: 'other', label: 'Otro', icon: '···' },
  ];

  // Computed
  totalAmount = computed(() =>
    this.selectedItems().reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    )
  );

  cartItemCount = computed(() =>
    this.selectedItems().reduce((sum, item) => sum + item.quantity, 0)
  );

  canSubmit = computed(
    () =>
      this.selectedItems().length > 0 &&
      !!this.selectedClient() &&
      !this.isSubmitting()
  );

  private previousTotal = 0;

  // Mirrors paymentForm.paidAmount so computed values react to it
  private paidAmountValue = signal(0);

  paidExceedsTotal = computed(() => this.paidAmountValue() > this.totalAmount());

  pendingAfterPayment = computed(() =>
    Math.max(0, this.totalAmount() - this.paidAmountValue())
  );

  constructor() {
    this.paymentForm.get('paidAmount')?.valueChanges.subscribe((v) =>
      this.paidAmountValue.set(Number(v) || 0)
    );

    // Search with debounce
    this.searchForm
      .get('searchTerm')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearching.set(true);
          return this.productsService.getProducts({
            searchTerm: term || undefined,
            isActive: true,
            pageSize: 20,
          });
        })
      )
      .subscribe({
        next: (response) => {
          this.products.set(response.products);
          this.isSearching.set(false);
        },
        error: () => {
          this.isSearching.set(false);
          this.toastService.show('Error al buscar productos', 'warning');
        },
      });

    // Auto-update paid amount when total changes
    effect(() => {
      const total = this.totalAmount();
      const currentPaidAmount =
        this.paymentForm.get('paidAmount')?.value || 0;

      if (
        total > 0 &&
        (currentPaidAmount === 0 ||
          Math.abs(currentPaidAmount - this.previousTotal) < 0.01)
      ) {
        this.paymentForm.patchValue(
          { paidAmount: total },
          { emitEvent: false }
        );
        this.paidAmountValue.set(total);
      }
      this.previousTotal = total;
    });
  }

  ngOnInit(): void {
    this.loadProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        document.body.classList.add('overflow-hidden');
        this.loadProducts();
        setTimeout(() => this.searchInput()?.nativeElement.focus());
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  @HostListener('document:keydown.escape')
  onEscKey(): void {
    if (this.open) {
      this.closeModal();
    }
  }

  // --- Client ---

  toggleClientMode(useGeneric: boolean): void {
    this.useGenericClient.set(useGeneric);
    if (useGeneric) {
      this.selectedClient.set(ProductSalesModal.GENERIC_CLIENT);
    } else {
      this.selectedClient.set(null);
    }
  }

  onClientSelected(client: Client): void {
    this.selectedClient.set(client);
  }

  onClientCleared(): void {
    this.selectedClient.set(null);
  }

  // --- Products ---

  loadProducts(): void {
    this.isLoading.set(true);
    this.productsService
      .getProducts({ isActive: true, pageSize: 50 })
      .subscribe({
        next: (response) => {
          this.products.set(response.products);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.toastService.show('Error al cargar productos', 'warning');
        },
      });
  }

  addProduct(product: Product): void {
    if (product.stock === 0) return;
    this.cartProducts.set(product.productId, product);

    const existing = this.selectedItems().find(
      (item) => item.productId === product.productId
    );

    if (existing) {
      if (existing.quantity >= product.stock) {
        this.toastService.show(
          `Stock disponible: ${product.stock}`,
          'warning'
        );
        return;
      }
      const updated = this.selectedItems().map((item) =>
        item.productId === product.productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      this.selectedItems.set(updated);
    } else {
      this.selectedItems.set([
        ...this.selectedItems(),
        {
          productId: product.productId,
          quantity: 1,
          unitPrice: product.price,
        },
      ]);
    }
  }

  removeItem(productId: string): void {
    this.selectedItems.set(
      this.selectedItems().filter((item) => item.productId !== productId)
    );
  }

  updateQuantity(productId: string, quantity: number): void {
    if (quantity <= 0) {
      this.removeItem(productId);
      return;
    }

    const product = this.findProduct(productId);
    if (!product) return;

    if (quantity > product.stock) {
      this.toastService.show(
        `Stock disponible: ${product.stock}`,
        'warning'
      );
      return;
    }

    const updated = this.selectedItems().map((item) =>
      item.productId === productId ? { ...item, quantity } : item
    );
    this.selectedItems.set(updated);
  }

  private findProduct(productId: string): Product | undefined {
    return (
      this.cartProducts.get(productId) ??
      this.products().find((p) => p.productId === productId)
    );
  }

  getProductName(productId: string): string {
    return this.findProduct(productId)?.name || 'Producto';
  }

  getProductStock(productId: string): number {
    return this.findProduct(productId)?.stock || 0;
  }

  quantityInCart(productId: string): number {
    return this.selectedItems().find((i) => i.productId === productId)?.quantity ?? 0;
  }

  getItemTotal(item: ProductSaleItem): number {
    return item.quantity * item.unitPrice;
  }

  getTotalAmount(): number {
    return this.totalAmount();
  }

  // --- Currency formatting ---

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  parseCurrency(value: string): number {
    if (!value) return 0;
    const cleaned = value.replace(/[^\d]/g, '');
    return cleaned ? parseInt(cleaned, 10) : 0;
  }

  onPaidAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const numericValue = input.value.replace(/[^\d]/g, '');
    if (numericValue) {
      const numValue = parseInt(numericValue, 10);
      this.paymentForm.patchValue(
        { paidAmount: numValue },
        { emitEvent: false }
      );
      this.paidAmountValue.set(numValue);
      input.value = this.formatCurrency(numValue);
    } else {
      this.paymentForm.patchValue({ paidAmount: 0 }, { emitEvent: false });
      this.paidAmountValue.set(0);
      input.value = '';
    }
  }

  onPaidAmountFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.paymentForm.get('paidAmount')?.value || 0;
    input.value = value.toString();
  }

  onPaidAmountBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.paymentForm.get('paidAmount')?.value || 0;
    input.value = this.formatCurrency(value);
  }

  getPaidAmountDisplay(): string {
    const value = this.paymentForm.get('paidAmount')?.value || 0;
    return this.formatCurrency(value);
  }

  // --- Submit ---

  submitSale(): void {
    const client = this.selectedClient();
    if (!client || this.selectedItems().length === 0) {
      if (!client) {
        this.toastService.show('Selecciona un cliente', 'warning');
      } else {
        this.toastService.show('Agrega al menos un producto', 'warning');
      }
      return;
    }

    if (this.paymentForm.invalid) {
      this.toastService.show('Completa los datos de pago', 'warning');
      return;
    }

    const totalAmount = this.getTotalAmount();
    const { paidAmount, paymentMethod, notes } =
      this.paymentForm.getRawValue();

    if (paidAmount && paidAmount > totalAmount) {
      this.toastService.show(
        'El monto pagado no puede ser mayor al total',
        'warning'
      );
      return;
    }

    this.isSubmitting.set(true);
    this.lastPaidAmount.set(Math.min(paidAmount || 0, totalAmount));

    const payload = {
      clientId: client.userId,
      items: this.selectedItems(),
      currency: 'COP',
    };

    this.paymentsService.createProductSale(payload).subscribe({
      next: (response) => {
        if (response.paymentId) {
          this.lastPaymentId.set(response.paymentId);
        }

        if (paidAmount && paidAmount > 0 && response.paymentId) {
          this.paymentsService
            .registerManualPayment(response.paymentId, {
              amount: Math.min(paidAmount, totalAmount),
              method: paymentMethod || 'cash',
              notes: notes?.trim() || undefined,
              useClientCredit: paymentMethod === 'credit',
            })
            .subscribe({
              next: () => {
                this.isSubmitting.set(false);
                this.lastSaleTotal.set(totalAmount);
                this.modalState.set('success');
                this.saleCompleted.emit();
              },
              error: (err) => {
                this.isSubmitting.set(false);
                this.lastSaleTotal.set(totalAmount);
                this.modalState.set('success');
                const message =
                  err.error?.message ||
                  'Venta registrada pero error al procesar el pago';
                this.toastService.show(message, 'warning');
                this.saleCompleted.emit();
              },
            });
        } else {
          this.isSubmitting.set(false);
          this.lastSaleTotal.set(totalAmount);
          this.modalState.set('success');
          this.saleCompleted.emit();
        }
      },
      error: (err) => {
        this.isSubmitting.set(false);
        const message =
          err.error?.message || 'Error al registrar la venta';
        this.toastService.show(message, 'warning');
      },
    });
  }

  // --- Receipt ---

  downloadReceipt(format: ReceiptFormat = 'half-letter'): void {
    const paymentId = this.lastPaymentId();
    if (!paymentId) {
      this.toastService.show('No hay comprobante disponible', 'warning');
      return;
    }

    this.isDownloadingReceipt.set(true);
    this.salesService.downloadReceipt(paymentId, format).subscribe({
      next: (blob) => {
        if (blob.size === 0) {
          blob
            .text()
            .then((text) => {
              try {
                const error = JSON.parse(text);
                this.toastService.show(
                  error.message || 'Error al generar el comprobante',
                  'error'
                );
              } catch {
                this.toastService.show(
                  'Error al generar el comprobante PDF',
                  'error'
                );
              }
            })
            .catch(() => {
              this.toastService.show(
                'Error al generar el comprobante PDF',
                'error'
              );
            });
          this.isDownloadingReceipt.set(false);
          return;
        }

        const pdfBlob =
          blob.type === 'application/pdf'
            ? blob
            : new Blob([blob], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprobante-${paymentId.slice(0, 8)}${format === 'ticket' ? '-tirilla' : ''}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.isDownloadingReceipt.set(false);
        this.toastService.show(
          'Comprobante descargado exitosamente',
          'success'
        );
      },
      error: (err) => {
        this.isDownloadingReceipt.set(false);
        if (err.error instanceof Blob) {
          err.error
            .text()
            .then((text: string) => {
              try {
                const error = JSON.parse(text);
                this.toastService.show(
                  error.message || 'Error al descargar el comprobante',
                  'error'
                );
              } catch {
                this.toastService.show(
                  'Error al descargar el comprobante',
                  'error'
                );
              }
            })
            .catch(() => {
              this.toastService.show(
                'Error al descargar el comprobante',
                'error'
              );
            });
        } else {
          const message =
            err.error?.message ||
            err.message ||
            'Error al descargar el comprobante';
          this.toastService.show(message, 'error');
        }
      },
    });
  }

  // --- Modal control ---

  closeModal(): void {
    this.resetState();
    this.close.emit();
  }

  startNewSale(): void {
    this.resetState();
    this.loadProducts();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  private resetState(): void {
    this.modalState.set('sale');
    this.mobilePane.set('catalog');
    this.selectedItems.set([]);
    this.cartProducts.clear();
    this.lastPaidAmount.set(0);
    this.useGenericClient.set(true);
    this.selectedClient.set(ProductSalesModal.GENERIC_CLIENT);
    this.lastPaymentId.set(null);
    this.lastSaleTotal.set(0);
    this.searchForm.patchValue({ searchTerm: '' });
    this.paymentForm.reset({
      paidAmount: 0,
      paymentMethod: 'cash',
      notes: '',
    });
    this.previousTotal = 0;
    this.paidAmountValue.set(0);
  }
}
